/**
 * InlineErrorBanner — single-line error banner shared by every connector
 * dialog (``ConnectAccountDialog``, ``SpreadsheetConnectDialog``, …).
 *
 * Why a primitive: every dialog needs to surface a transient BE/UX error
 * INSIDE the dialog (toasts disappear; the user can't act on what they
 * can't read). Without this component the same 11-line ``role="alert"``
 * block was copy-pasted across files — a drift hazard the moment we
 * change the icon size, error tokens or aria contract.
 *
 *  - ``message === null`` (or empty) → renders nothing. Callers can pass
 *    raw state without wrapping in a conditional.
 *  - Otherwise: ``role="alert" aria-live="polite"`` + AlertCircle icon
 *    + Ando error tokens. Matches the previous inline JSX in
 *    ``ConnectAccountDialog.tsx`` 1:1.
 */

import { cn } from "@ando/ui";
import { AlertCircle } from "lucide-react";
import type * as React from "react";

export interface InlineErrorBannerProps {
  /** Error copy. ``null`` / empty string → renders nothing. */
  readonly message: string | null;
  /** Optional className override (e.g. extra top margin). */
  readonly className?: string;
}

export const InlineErrorBanner: React.FC<InlineErrorBannerProps> = ({ message, className }) => {
  if (!message) return null;
  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        "mt-3 flex items-start gap-2 rounded-md border border-error/30 bg-error/[0.08] px-3 py-2 text-[12px] text-error",
        className,
      )}
    >
      <AlertCircle className="h-3.5 w-3.5 shrink-0 translate-y-px" aria-hidden />
      <span className="min-w-0">{message}</span>
    </div>
  );
};
InlineErrorBanner.displayName = "InlineErrorBanner";
